import { useState } from 'react';

function addMonths(dateStr, months) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  d.setDate(d.getDate() - 1); // end date = one day before anniversary
  return d.toISOString().split('T')[0];
}

function fmtDisplay(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return dateStr; }
}

export default function StepTerms({ data, onChange, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  const set = (key, val) => onChange({ ...data, [key]: val });

  const handleStartDate = (val) => {
    const duration = parseInt(data.duration) || 12;
    onChange({ ...data, startDate: val, endDate: addMonths(val, duration) });
  };

  const handleDuration = (val) => {
    const months = parseInt(val) || 0;
    const end = data.startDate ? addMonths(data.startDate, months) : '';
    onChange({ ...data, duration: val, endDate: end });
  };

  const validate = () => {
    const e = {};
    if (!data.brands || data.brands.length === 0) e.brands = 'Select at least one brand';
    if (!data.startDate) e.startDate = 'Required';
    if (!data.duration || parseInt(data.duration) < 1) e.duration = 'Enter a valid duration';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validate()) onNext(); };

  const today = new Date().toISOString().split('T')[0];
  const duration = data.duration || '12';
  const endDateDisplay = data.endDate ? fmtDisplay(data.endDate) : '—';

  return (
    <div>
      {/* Agreement period */}
      <div className="card">
        <div className="card-title">Agreement period</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div className={`field${errors.startDate ? ' has-error' : ''}`} style={{ flex: 1 }}>
            <label>Start date <span className="req">*</span></label>
            <input
              type="date"
              value={data.startDate || today}
              onChange={e => handleStartDate(e.target.value)}
            />
            {errors.startDate && <div className="field-error">{errors.startDate}</div>}
          </div>
          <div className={`field${errors.duration ? ' has-error' : ''}`} style={{ width: 110, flexShrink: 0 }}>
            <label>Months <span className="req">*</span></label>
            <input
              type="number"
              min="1"
              max="60"
              value={duration}
              onChange={e => handleDuration(e.target.value)}
              placeholder="12"
              style={{ textAlign: 'center' }}
            />
            {errors.duration && <div className="field-error">{errors.duration}</div>}
          </div>
        </div>
        {data.endDate && (
          <div style={{
            marginTop: 12, padding: '10px 14px',
            background: 'var(--surface-warm)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)', fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <span style={{ color: 'var(--muted)' }}>Contract ends</span>
            <span style={{ fontWeight: 500 }}>{endDateDisplay}</span>
          </div>
        )}
      </div>

      {/* Brand checkboxes */}
      <div className="card">
        <div className="card-title">Product brands <span className="req">*</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {['Revitalash', 'La Roche-Posay', 'CeraVe', 'Madara Cosmetics'].map(brand => (
            <label key={brand} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={(data.brands || []).includes(brand)}
                onChange={e => {
                  const current = data.brands || [];
                  const updated = e.target.checked
                    ? [...current, brand]
                    : current.filter(b => b !== brand);
                  set('brands', updated);
                }}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--ink)' }}
              />
              <span style={{ fontSize: 14 }}>{brand}</span>
            </label>
          ))}
        </div>
        {(!data.brands || data.brands.length === 0) && (
          <div className="field-error" style={{ marginTop: 8 }}>Select at least one brand</div>
        )}
      </div>

      {/* Payment & credit */}
      <div className="card">
        <div className="card-title">Payment &amp; credit</div>
        <div className="field-grid">
          <div className="field">
            <label>Credit term</label>
            <select value={data.creditTerm || '30'} onChange={e => set('creditTerm', e.target.value)}>
              <option value="0">Due on Receipt (Upfront)</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="45">45 days</option>
              <option value="60">60 days</option>
            </select>
          </div>
          <div className="field">
            <label>Accepted payment methods</label>
            <select value={data.paymentMethod || ''} onChange={e => set('paymentMethod', e.target.value)}>
              <option value="credit card, bank transfer, and online payment as specified in the invoice">Credit card + bank transfer + online</option>
              <option value="bank transfer as specified in the invoice">Bank transfer only</option>
              <option value="credit card and online payment as specified in the invoice">Credit card + online</option>
            </select>
          </div>
        </div>
      </div>

      {/* Discount */}
      <div className="card">
        <div className="card-title">Account margin / structural discount</div>
        <div className="field-grid">
          <div className="field">
            <label>Discount %</label>
            <select value={data.discount || '50'} onChange={e => set('discount', e.target.value)}>
              <option value="0">0% — no discount</option>
              <option value="10">10%</option>
              <option value="15">15%</option>
              <option value="20">20%</option>
              <option value="25">25%</option>
              <option value="30">30%</option>
              <option value="35">35%</option>
              <option value="40">40%</option>
              <option value="45">45%</option>
              <option value="50">50% (default)</option>
              <option value="55">55%</option>
              <option value="60">60%</option>
              <option value="custom">Custom…</option>
            </select>
          </div>
          {data.discount === 'custom' && (
            <div className="field">
              <label>Custom %</label>
              <input type="number" min="0" max="100" step="0.5" placeholder="Enter %" onChange={e => set('discount', e.target.value)} />
            </div>
          )}
        </div>
      </div>

      {/* GST */}
      <div className="card">
        <div className="card-title">Tax details</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
          <p style={{ fontSize: 14, fontWeight: 500 }}>Customer is GST registered</p>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 10 }}>
            <div
              onClick={() => set('gstRegistered', !data.gstRegistered)}
              style={{
                width: 44, height: 24, borderRadius: 12,
                background: data.gstRegistered ? 'var(--success)' : 'var(--border)',
                position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0,
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: 'white',
                position: 'absolute', top: 3, left: data.gstRegistered ? 23 : 3,
                transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
            <span style={{ fontSize: 13, color: data.gstRegistered ? 'var(--success)' : 'var(--muted)' }}>
              {data.gstRegistered ? 'Yes' : 'No'}
            </span>
          </label>
        </div>
      </div>

      <div className="row between">
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={handleNext}>Review →</button>
      </div>
    </div>
  );
}
